import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, Droplets, Heart, Sparkles, Truck, ShoppingBag, ChevronRight, Star, Quote, TreePine, HeartHandshake, GitCompareArrows, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { API, useCart, useAuth } from "@/App";
import { toast } from "sonner";
import { LoginPromptDialog } from "@/components/LoginPromptDialog";
import { SEO } from "@/components/SEO";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } }
};

const benefits = [
  {
    icon: Sparkles,
    title: "40x More Nutrients",
    description: "Microgreens pack up to 40 times more vitamins and minerals than mature plants."
  },
  {
    icon: Heart,
    title: "Supports Immunity",
    description: "Rich in antioxidants and phytonutrients that strengthen your immune system."
  },
  {
    icon: Leaf,
    title: "Enhances Flavor",
    description: "Add vibrant colors and intense flavors to elevate any dish."
  },
  {
    icon: Droplets,
    title: "Clean Plant Nutrition",
    description: "100% chemical-free, locally grown with sustainable practices."
  }
];

const steps = [
  { number: "01", title: "We Grow Fresh", description: "Premium seeds planted in organic medium with care and precision." },
  { number: "02", title: "We Harvest to Order", description: "Cut fresh on the day of delivery for maximum nutrition." },
  { number: "03", title: "Delivered to Your Kitchen", description: "Temperature-controlled packaging ensures perfect freshness." }
];

const healthBenefits = [
  { title: "Immunity", icon: "🛡️", color: "bg-green-50 border-green-200" },
  { title: "Detox", icon: "🌿", color: "bg-emerald-50 border-emerald-200" },
  { title: "Heart Health", icon: "❤️", color: "bg-red-50 border-red-200" },
  { title: "Digestive Health", icon: "🌱", color: "bg-lime-50 border-lime-200" }
];

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [spotlight, setSpotlight] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, testimonialsRes, spotlightRes, blogRes] = await Promise.all([
          axios.get(`${API}/products?featured=true`),
          axios.get(`${API}/testimonials?limit=3`),
          axios.get(`${API}/spotlight?limit=6`),
          axios.get(`${API}/blog?limit=3`)
        ]);
        setProducts(productsRes.data);
        setTestimonials(testimonialsRes.data);
        setSpotlight(spotlightRes.data);
        setBlogPosts(blogRes.data);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAddToCart = async (productId) => {
    if (!user) {
      setShowLoginPrompt(true);
      return;
    }
    try {
      await addToCart(productId);
      toast.success("Added to cart!");
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  return (
    <div data-testid="home-page">
      <SEO 
        title="Fresh Microgreens Delivered" 
        description="Farm-grown nutrient-dense microgreens delivered fresh to your kitchen. 4-40x more nutrients, 100% organic, same-day harvest. Order from Praroha Greens."
        keywords="microgreens, organic microgreens, fresh microgreens Hyderabad, buy microgreens online, nutrient dense greens, praroha greens"
        path="/"
        keywords="microgreens, organic, fresh delivery, nutrient-dense, farm to table, Praroha Greens"
        path="/"
      />
      {/* Hero Section - Premium Redesign */}
      <section className="relative min-h-[80vh] sm:min-h-[85vh] overflow-hidden pt-16 md:pt-20 lg:pt-24">
        {/* Background with gradient overlay */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_0c320f8b-e86f-46ef-9636-dfcf52e2f03a/artifacts/amf8ovjc_freepik__create-an-ultrarealistic-indoor-microgreens-farmin__11026.png"
            alt="Indoor microgreens farming facility"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-praroha-forest via-praroha-forest/90 to-praroha-forest/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-praroha-forest/60 via-transparent to-praroha-forest/30" />
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-10 w-72 h-72 bg-praroha-sage/20 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-praroha-terracotta/10 rounded-full blur-3xl opacity-30" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[85vh] flex items-center">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full py-8 md:py-12">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-white"
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full mb-6"
              >
                <span className="w-2 h-2 bg-praroha-terracotta rounded-full animate-pulse" />
                <span className="text-sm font-medium text-white/90">Nutrient-Rich, Naturally!</span>
              </motion.div>
              
              <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.1] mb-4 sm:mb-6">
                Fresh Microgreens.
                <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-praroha-sage to-green-300">
                  Pure Nutrition.
                </span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl text-white/80 max-w-xl mb-6 sm:mb-8 leading-relaxed">
                Farm-grown nutrient dense microgreens delivered fresh to your kitchen. 
                Experience the power of nature's most concentrated nutrition.
              </p>
              
              <div className="flex flex-wrap gap-3 sm:gap-4 mb-6 sm:mb-8">
                <Link to="/products" data-testid="hero-shop-btn">
                  <Button className="bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full px-6 py-5 sm:px-8 sm:py-6 text-sm sm:text-lg font-medium shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    Shop Microgreens
                    <ShoppingBag className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </Link>
                <Link to="/about-microgreens" data-testid="hero-learn-btn">
                  <Button variant="outline" className="rounded-full px-6 py-5 sm:px-8 sm:py-6 text-sm sm:text-lg font-medium border-white/30 text-white hover:bg-white/10 hover:border-white/50">
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
                  </Button>
                </Link>
              </div>
              
              {/* Stats Row */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="grid grid-cols-3 gap-3 sm:gap-6"
              >
                <div className="text-center">
                  <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-praroha-terracotta">40x</p>
                  <p className="text-xs sm:text-sm text-white/70 mt-1">More Nutrients</p>
                </div>
                <div className="text-center border-x border-white/20">
                  <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-praroha-terracotta">100%</p>
                  <p className="text-xs sm:text-sm text-white/70 mt-1">Chemical Free</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold text-praroha-terracotta">04hr</p>
                  <p className="text-xs sm:text-sm text-white/70 mt-1">Fresh Delivery</p>
                </div>
              </motion.div>
            </motion.div>
            
            {/* Right - Product Showcase Cards */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="hidden lg:block relative"
            >
              <div className="relative">
                {/* Main featured card */}
                <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl p-6 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                  <div className="aspect-square rounded-2xl overflow-hidden mb-4">
                    <img
                      src="https://customer-assets.emergentagent.com/job_farm-to-table-greens-1/artifacts/y7ix5jfl_freepik__create-an-ultrarealistic-macro-photograph-of-a-sin__11027.png"
                      alt="Fresh Microgreens"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      data-testid="hero-image"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-xl font-semibold text-praroha-forest">Fresh Microgreens</h3>
                      <p className="text-sm text-muted-foreground">Nutrient Powerhouse</p>
                    </div>
                    <span className="font-heading text-lg font-bold text-praroha-terracotta">100% Chemical Free</span>
                  </div>
                </div>
                
                {/* Floating badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  className="absolute -top-4 -right-4 bg-praroha-terracotta text-white px-4 py-2 rounded-full shadow-lg"
                >
                  <span className="text-sm font-medium">Fresh Daily</span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg className="relative block w-full h-20" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="hsl(var(--background))" />
          </svg>
        </div>
      </section>

      {/* About Microgreens Info Section */}
      <section className="py-16 md:py-20 bg-background" data-testid="microgreens-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* What are Microgreens */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
            >
              <Card className="h-full bg-white border border-border/50 hover:shadow-soft transition-all duration-300 overflow-hidden">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-praroha-forest/10 flex items-center justify-center mb-5">
                    <Leaf className="w-7 h-7 text-praroha-forest" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-praroha-forest mb-4">What are Microgreens?</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Microgreens are young seedlings of edible plants, harvested at an early stage, typically 
                    <span className="font-semibold text-praroha-forest"> 7 to 21 days</span> after germination. They're 
                    packed with flavor and nutrition in every tiny leaf.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full bg-white border border-border/50 hover:shadow-soft transition-all duration-300 overflow-hidden">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-praroha-forest/10 flex items-center justify-center mb-5">
                    <Sparkles className="w-7 h-7 text-praroha-forest" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-praroha-forest mb-4">A Culinary Legacy</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Microgreens have roots in fine dining since the 
                    <span className="font-semibold text-praroha-forest"> 1980s</span>, initially popularized in 
                    California. They've since expanded from exclusive restaurants into mainstream culinary practices worldwide.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Nutrition */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="h-full bg-white border border-border/50 hover:shadow-soft transition-all duration-300 overflow-hidden">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-praroha-forest/10 flex items-center justify-center mb-5">
                    <Heart className="w-7 h-7 text-praroha-forest" />
                  </div>
                  <h3 className="font-heading text-2xl font-semibold text-praroha-forest mb-4">Nutrient Powerhouse</h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Research indicates that many microgreens contain 
                    <span className="font-semibold text-praroha-forest"> higher concentrations</span> of essential 
                    nutrients compared to mature plants.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-praroha-forest/10 rounded-full text-sm font-medium text-praroha-forest">Vitamins</span>
                    <span className="px-3 py-1 bg-praroha-forest/10 rounded-full text-sm font-medium text-praroha-forest">Minerals</span>
                    <span className="px-3 py-1 bg-praroha-forest/10 rounded-full text-sm font-medium text-praroha-forest">Fibre</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-16 md:py-20 bg-praroha-forest" data-testid="vision-mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Our Vision */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="bg-praroha-sage/30 border border-praroha-sage/40 rounded-3xl p-8 md:p-10 h-full relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 rounded-full mb-6">
                    <div className="w-2 h-2 bg-white rounded-full" />
                    <span className="text-sm font-medium text-white">Our Vision</span>
                  </div>
                  <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                    A Greener Future
                  </h3>
                  <p className="text-white/80 text-lg leading-relaxed">
                    To cultivate a healthier, greener future by making fresh, sustainable microgreens accessible to every home and community.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Our Mission */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative"
            >
              <div className="bg-praroha-sage/30 border border-praroha-sage/40 rounded-3xl p-8 md:p-10 h-full relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 rounded-full mb-6">
                    <div className="w-2 h-2 bg-praroha-terracotta rounded-full" />
                    <span className="text-sm font-medium text-white">Our Mission</span>
                  </div>
                  <h3 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                    Empowering Communities
                  </h3>
                  <p className="text-white/80 text-lg leading-relaxed">
                    Praroha Greens is committed to growing and delivering premium-quality microgreens that are nutrient-rich, eco-friendly, and naturally cultivated. We empower communities through sustainable agriculture, education, and innovation in urban farming.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Microgreens Section */}
      <section className="section-padding bg-background" data-testid="why-microgreens-section">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={stagger}
            className="text-center mb-10"
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block px-4 py-2 bg-praroha-sage/10 text-praroha-sage text-sm font-medium rounded-full mb-4"
            >
              Why Choose Microgreens
            </motion.span>
            <motion.h2
              variants={fadeInUp}
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest mb-4"
            >
              Nature's Most Powerful Nutrition
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-muted-foreground max-w-2xl mx-auto">
              Microgreens are young vegetable greens harvested just after the first leaves develop. 
              They're packed with flavor, color, and concentrated nutrients.
            </motion.p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="group h-full bg-white hover:shadow-hover transition-all duration-300 border-border/50 hover:border-praroha-sage/30">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-2xl bg-praroha-forest/10 flex items-center justify-center mb-4 group-hover:bg-praroha-forest group-hover:scale-110 transition-all duration-300">
                      <benefit.icon className="w-7 h-7 text-praroha-forest group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-praroha-forest mb-2">
                      {benefit.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-16 md:py-20 bg-praroha-forest" data-testid="core-values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-block px-4 py-2 bg-white/10 text-white/90 text-sm font-medium tracking-wider uppercase rounded-full mb-4">
              What Drives Us
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              Core Values
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Sustainability */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
            >
              <div className="bg-praroha-sage/20 border border-praroha-sage/30 rounded-2xl p-8 h-full text-center hover:bg-praroha-sage/30 transition-colors">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center">
                  <TreePine className="w-10 h-10 text-praroha-sage" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">Sustainability</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  We grow responsibly, conserving resources and nurturing the planet for future generations.
                </p>
              </div>
            </motion.div>

            {/* Wellness */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="bg-praroha-sage/20 border border-praroha-sage/30 rounded-2xl p-8 h-full text-center hover:bg-praroha-sage/30 transition-colors">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center">
                  <svg className="w-14 h-14 text-praroha-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 21c-4-4-8-7-8-11a8 8 0 0 1 16 0c0 4-4 7-8 11z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">Wellness</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  We promote health and nutrition through naturally grown, chemical-free microgreens.
                </p>
              </div>
            </motion.div>

            {/* Innovation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-praroha-sage/20 border border-praroha-sage/30 rounded-2xl p-8 h-full text-center hover:bg-praroha-sage/30 transition-colors">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center">
                  <svg className="w-14 h-14 text-praroha-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 18h6M10 22h4M12 2v1M12 6a5 5 0 0 0-5 5c0 2.76 2.5 5 5 8 2.5-3 5-5.24 5-8a5 5 0 0 0-5-5z" />
                    <circle cx="12" cy="11" r="2" />
                  </svg>
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">Innovation</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  We embrace new technologies and methods to improve how food is grown and consumed.
                </p>
              </div>
            </motion.div>

            {/* Integrity */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="bg-praroha-sage/20 border border-praroha-sage/30 rounded-2xl p-8 h-full text-center hover:bg-praroha-sage/30 transition-colors">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center">
                  <svg className="w-14 h-14 text-praroha-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M4 7h16M4 7v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7M4 7l2-4h12l2 4" />
                    <path d="M9 11h6M9 15h6" />
                  </svg>
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">Integrity</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  We uphold transparency, trust, and ethical practices in every aspect of our work.
                </p>
              </div>
            </motion.div>

            {/* Community Empowerment */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <div className="bg-praroha-sage/20 border border-praroha-sage/30 rounded-2xl p-8 h-full text-center hover:bg-praroha-sage/30 transition-colors">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center">
                  <HeartHandshake className="w-10 h-10 text-praroha-sage" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">Community Empowerment</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  We support local farmers, youth, and urban dwellers through training and green entrepreneurship.
                </p>
              </div>
            </motion.div>

            {/* Quality Excellence */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
            >
              <div className="bg-praroha-sage/20 border border-praroha-sage/30 rounded-2xl p-8 h-full text-center hover:bg-praroha-sage/30 transition-colors">
                <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center">
                  <svg className="w-14 h-14 text-praroha-sage" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 2l2 6h6l-5 4 2 6-5-4-5 4 2-6-5-4h6z" />
                  </svg>
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">Quality Excellence</h3>
                <p className="text-white/70 text-sm leading-relaxed">
                  We maintain the highest standards in freshness, hygiene, and taste.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Microgreens vs Sprouts Section */}
      <section className="py-10 md:py-12 bg-background" data-testid="microgreens-vs-sprouts-section">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <span className="inline-block px-4 py-2 bg-praroha-sage/10 text-praroha-sage text-sm font-medium rounded-full mb-4">
              Know the Difference
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest mb-3">
              Microgreens vs Sprouts
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Distinguishing Characteristics
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Leaf className="w-7 h-7 text-praroha-forest group-hover:text-white transition-colors" />,
                title: "Comparison",
                description: "A side-by-side visual analysis highlighting the differences between microgreens and sprouts, including their growth stages and characteristics.",
                delay: 0
              },
              {
                icon: <Droplets className="w-7 h-7 text-praroha-forest group-hover:text-white transition-colors" />,
                title: "Growth Medium",
                description: "Microgreens are typically grown in soil, whereas sprouts are germinated in water. The choice of medium affects their nutrient profiles.",
                delay: 0.1
              },
              {
                icon: <Heart className="w-7 h-7 text-praroha-forest group-hover:text-white transition-colors" />,
                title: "Nutritional Value",
                description: "Microgreens contain higher vitamins and antioxidants compared to sprouts, distinguishing factors that cater to the health focused consumers.",
                delay: 0.2
              },
              {
                icon: <ShieldCheck className="w-7 h-7 text-praroha-forest group-hover:text-white transition-colors" />,
                title: "Safety",
                description: "Microgreens are generally deemed safer to consume due to their growing conditions while sprouts can carry a risk of bacteria if not managed properly.",
                delay: 0.3
              }
            ].map((card) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: card.delay }}
              >
                <Card className="group h-full bg-white hover:shadow-hover transition-all duration-300 border-border/50 hover:border-praroha-sage/30">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 rounded-2xl bg-praroha-forest/10 flex items-center justify-center mb-4 group-hover:bg-praroha-forest group-hover:scale-110 transition-all duration-300">
                      {card.icon}
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-praroha-forest mb-2">
                      {card.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {card.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section className="py-10 md:py-12 bg-gradient-to-b from-background to-praroha-cream/30" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8">
            <div>
              <span className="inline-block px-4 py-2 bg-praroha-terracotta/10 text-praroha-terracotta text-sm font-medium rounded-full mb-4">
                Our Products
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest">
                Featured Microgreens
              </h2>
            </div>
            <Link to="/products" className="mt-4 md:mt-0" data-testid="view-all-products-link">
              <Button variant="ghost" className="text-praroha-forest hover:text-praroha-sage group">
                View All Products
                <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-6">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-80 bg-muted animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-6">
              {products.slice(0, 5).map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex"
                >
                  <Card className="group flex flex-col w-full bg-white overflow-hidden border-border/40 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-500" data-testid={`product-card-${product.slug}`}>
                    <Link to={`/products/${product.slug}`}>
                      <div className="aspect-square overflow-hidden bg-praroha-cream/50">
                        <img
                          src={product.image}
                          alt={`${product.name} - Fresh Organic Microgreens`}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                    </Link>
                    <CardContent className="p-3 sm:p-4 flex flex-col flex-1">
                      <Link to={`/products/${product.slug}`}>
                        <h3 className="font-heading text-sm sm:text-base font-semibold text-praroha-forest mb-1 group-hover:text-praroha-sage transition-colors line-clamp-2">
                          {product.name}
                        </h3>
                      </Link>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2 flex-1">
                        {product.short_description}
                      </p>
                      <div className="flex items-center justify-between mt-auto gap-1">
                        <span className="font-semibold text-praroha-forest text-sm sm:text-base">
                            ₹{product.price} <span className="text-[10px] sm:text-xs font-normal text-muted-foreground">/ {product.weight || '50g'}</span>
                          </span>
                        <Button
                          size="sm"
                          className="bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full text-[10px] sm:text-xs px-2 sm:px-3 h-7 sm:h-8"
                          onClick={() => handleAddToCart(product.id)}
                          data-testid={`add-to-cart-${product.slug}`}
                        >
                          Add to Cart
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section-padding bg-praroha-forest text-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-2 bg-white/10 text-white text-sm font-medium rounded-full mb-4">
              Our Process
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold mb-4">
              How It Works
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              From seed to your table in just a few days. Experience the freshest microgreens possible.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white/10 mb-6">
                    <span className="font-heading text-3xl font-bold text-praroha-terracotta">
                      {step.number}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-white/70">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-px bg-gradient-to-r from-white/30 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Health Benefits Section */}
      <section className="section-padding bg-background" data-testid="health-benefits-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-8">
            <span className="inline-block px-4 py-2 bg-praroha-sage/10 text-praroha-sage text-sm font-medium rounded-full mb-4">
              Health Benefits
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest">
              Nourish Your Body
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {healthBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`h-full ${benefit.color} border hover:shadow-soft transition-all duration-300`}>
                  <CardContent className="p-6 text-center">
                    <span className="text-4xl mb-4 block">{benefit.icon}</span>
                    <h3 className="font-heading text-xl font-semibold text-praroha-forest">
                      {benefit.title}
                    </h3>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-background to-praroha-cream/50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10">
            <div>
              <span className="inline-block px-4 py-2 bg-praroha-terracotta/10 text-praroha-terracotta text-sm font-medium rounded-full mb-4">
                Testimonials
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest">
                What Our Customers Say
              </h2>
            </div>
            <Link to="/testimonials" className="mt-4 md:mt-0">
              <Button variant="ghost" className="text-praroha-forest hover:text-praroha-sage group">
                View All Reviews
                <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          {testimonials.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.12 }}
                >
                  <Card className="group h-full bg-white border-border/50 hover:border-praroha-sage/30 hover:shadow-hover transition-all duration-300">
                    <CardContent className="p-6 flex flex-col h-full">
                      {/* Header */}
                      <div className="flex items-center gap-4 mb-5 pb-5 border-b border-border/50">
                        <div className="w-14 h-14 shrink-0 rounded-full bg-praroha-forest/10 flex items-center justify-center overflow-hidden group-hover:bg-praroha-forest transition-colors duration-300">
                          {testimonial.image ? (
                            <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xl font-heading font-bold text-praroha-forest group-hover:text-white transition-colors">{testimonial.name.split(' ').slice(-1)[0].charAt(0)}</span>
                          )}
                        </div>
                        <div>
                          <h4 className="font-heading text-base font-semibold text-praroha-forest leading-tight">
                            {testimonial.name}
                          </h4>
                          <p className="text-xs text-praroha-sage mt-1 leading-snug">
                            {testimonial.role}
                          </p>
                        </div>
                      </div>
                      {/* Content */}
                      <Quote className="w-7 h-7 text-praroha-terracotta/20 mb-3 shrink-0" />
                      <p className="text-muted-foreground text-sm leading-relaxed flex-1">
                        {testimonial.content}
                      </p>
                      <div className="flex gap-1 mt-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-praroha-terracotta text-praroha-terracotta" />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground">No testimonials yet.</p>
          )}
        </div>
      </section>

      {/* Customer Spotlight Preview */}
      {spotlight.length > 0 && (
        <section className="section-padding bg-background" data-testid="spotlight-preview-section">
          <div className="max-w-7xl mx-auto container-padding">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8">
              <div>
                <span className="inline-block px-4 py-2 bg-praroha-sage/10 text-praroha-sage text-sm font-medium rounded-full mb-4">
                  Community
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest">
                  Customer Spotlight
                </h2>
              </div>
              <Link to="/spotlight" className="mt-4 md:mt-0" data-testid="see-community-recipes-link">
                <Button variant="ghost" className="text-praroha-forest hover:text-praroha-sage group">
                  See Community Recipes
                  <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {spotlight.slice(0, 6).map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="aspect-square overflow-hidden rounded-xl group cursor-pointer"
                >
                  <img
                    src={post.image}
                    alt={post.dish_name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Preview */}
      {blogPosts.length > 0 && (
        <section className="section-padding bg-gradient-to-b from-background to-praroha-cream/30" data-testid="blog-preview-section">
          <div className="max-w-7xl mx-auto container-padding">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-8">
              <div>
                <span className="inline-block px-4 py-2 bg-praroha-terracotta/10 text-praroha-terracotta text-sm font-medium rounded-full mb-4">
                  From Our Blog
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-praroha-forest">
                  Latest Articles
                </h2>
              </div>
              <Link to="/blog" className="mt-4 md:mt-0">
                <Button variant="ghost" className="text-praroha-forest hover:text-praroha-sage group">
                  View All Articles
                  <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <Card className="group h-full overflow-hidden border-border/40 hover:shadow-hover transition-all duration-500">
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <CardContent className="p-6">
                        <span className="text-xs font-medium text-praroha-terracotta">
                          {post.category}
                        </span>
                        <h3 className="font-heading text-lg font-semibold text-praroha-forest mt-2 mb-2 group-hover:text-praroha-sage transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                        <p className="text-muted-foreground text-sm line-clamp-2">
                          {post.excerpt}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-padding bg-praroha-sage" data-testid="cta-section">
        <div className="max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-4">
              Ready to Transform Your Health?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Start your journey to better nutrition today. Order fresh microgreens and experience the difference.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/products" data-testid="cta-shop-btn">
                <Button className="bg-white text-praroha-sage hover:bg-white/90 rounded-full px-8 py-6 text-lg font-medium shadow-soft hover:shadow-hover hover:-translate-y-1 transition-all duration-300">
                  Shop Now
                  <ShoppingBag className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full px-8 py-6 text-lg font-medium border-white/30 text-white hover:bg-white/10">
                  Contact Us
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      <LoginPromptDialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt} />
    </div>
  );
}
